import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Truck, Plus, Search, Loader2, Lock, CheckCircle2, X, Printer } from "lucide-react";

const inp = "w-full bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-sm outline-none focus:border-[#F97316] font-mono text-[#F3F4F6]";
const lbl = "font-mono text-[10px] text-[#9CA3AF] uppercase mb-1 block";

// TOW BOARD — vehicles towed: make/model/year, reason for tow, hold & release chain.
export const TowBoard = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(null); // tow being released
  const [releaseTo, setReleaseTo] = useState("");
  const empty = { plate: "", state: "", make: "", model: "", year: "", color: "", vin: "", owner_name: "", reason: "", tow_company: "", towed_from: "", towed_to: "", hold: false, incident_cc: "", notes: "", tow_datetime: "" };
  // eslint-disable-next-line no-unused-vars
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const load = () => client.get("/tows", { params: { q, status } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const r = await client.post("/tows", f);
      toast.success(`Tow logged — ${r.data.tow_number}`);
      setShow(false); setF(empty); load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Tow entry failed"); }
    finally { setBusy(false); }
  };
  const release = async (t) => {
    try {
      await client.post(`/tows/${t.id}/release`, { released_to: releaseTo });
      toast.success(`${t.tow_number} released`);
      setReleasing(null); setReleaseTo(""); load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Release failed"); }
  };

  const inTow = (data?.tows || []).filter((t) => t.status === "in_tow");
  return (
    <div data-testid="tow-board">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-[#9CA3AF]" />
          <input data-testid="tow-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search plate, tow #, make, owner, company, Case#…" className={inp + " pl-8"} />
        </div>
        <select data-testid="tow-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={inp + " w-auto"}>
          <option value="">All</option><option value="in_tow">In Tow</option>
          <option value="hold">On Hold</option><option value="released">Released</option>
        </select>
        <span className="font-mono text-[10px] text-[#9CA3AF]">{inTow.length} IN TOW · {data?.total ?? 0} TOTAL</span>
        <button data-testid="tow-new" onClick={() => setShow(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#F97316] text-black font-head font-semibold text-sm hover:bg-[#ea6a0c]">
          <Plus size={15} /> Log Tow
        </button>
      </div>

      {show && (
        <div className="border border-[#F97316]/40 bg-[#141518] p-4 mb-3" data-testid="tow-form">
          <div className="font-head font-bold text-sm mb-3 flex items-center gap-2"><Truck size={15} className="text-[#F97316]" /> NEW TOW RECORD
            <button onClick={() => setShow(false)} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={16} /></button></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <div><span className={lbl}>Plate *</span><input data-testid="tow-plate" value={f.plate} onChange={set("plate")} className={inp} /></div>
            <div><span className={lbl}>State</span><input value={f.state} onChange={set("state")} className={inp} placeholder="PA / MD" /></div>
            <div><span className={lbl}>Year</span><input data-testid="tow-year" value={f.year} onChange={set("year")} className={inp} /></div>
            <div><span className={lbl}>Color</span><input value={f.color} onChange={set("color")} className={inp} /></div>
            <div><span className={lbl}>Make *</span><input data-testid="tow-make" value={f.make} onChange={set("make")} className={inp} /></div>
            <div><span className={lbl}>Model *</span><input data-testid="tow-model" value={f.model} onChange={set("model")} className={inp} /></div>
            <div><span className={lbl}>Tow date & time (backdate for backlog)</span><input data-testid="tow-datetime" type="datetime-local" value={f.tow_datetime} onChange={set("tow_datetime")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>VIN</span><input value={f.vin} onChange={set("vin")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Reason for tow *</span>
              <select data-testid="tow-reason" value={f.reason} onChange={set("reason")} className={inp}>
                <option value="">Select reason…</option>
                {(data?.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select></div>
            <div className="col-span-2"><span className={lbl}>Registered owner</span><input value={f.owner_name} onChange={set("owner_name")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Towed from</span><input value={f.towed_from} onChange={set("towed_from")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Towed to (lot / impound)</span><input value={f.towed_to} onChange={set("towed_to")} className={inp} /></div>
            <div><span className={lbl}>Tow company</span><input value={f.tow_company} onChange={set("tow_company")} className={inp} /></div>
            <div><span className={lbl}>Linked CC #</span><input value={f.incident_cc} onChange={set("incident_cc")} className={inp} /></div>
            <div className="col-span-2"><span className={lbl}>Notes</span><input value={f.notes} onChange={set("notes")} className={inp} /></div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-xs text-[#F59E0B] cursor-pointer font-mono">
            <input data-testid="tow-hold" type="checkbox" checked={f.hold} onChange={set("hold")} />
            <Lock size={12} /> EVIDENCE / INVESTIGATIVE HOLD — supervisor release required
          </label>
          <button data-testid="tow-save" onClick={save} disabled={busy}
            className="mt-3 flex items-center gap-1.5 px-4 py-2 bg-[#F97316] text-black font-head font-bold text-sm disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />} LOG TOW
          </button>
        </div>
      )}

      <table className="w-full text-left">
        <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11] sticky top-0">
          <tr><th className="px-3 py-2">Tow #</th><th className="px-2">Vehicle</th><th className="px-2">Plate</th>
            <th className="px-2">Reason</th><th className="px-2">From → To</th><th className="px-2">Company</th>
            <th className="px-2">CC #</th><th className="px-2">Status</th><th className="px-2"></th></tr>
        </thead>
        <tbody data-testid="tow-rows">
          {(data?.tows || []).map((t) => (
            <tr key={t.id} data-testid={`tow-row-${t.tow_number}`} className="border-b border-[#2C2D35] text-sm">
              <td className="px-3 py-2 font-mono text-[11px] text-[#F97316]">{t.tow_number}</td>
              <td className="px-2">{[t.year, t.color, t.make, t.model].filter(Boolean).join(" ") || "—"}</td>
              <td className="px-2 font-mono text-[11px]">{t.plate}{t.state ? `/${t.state}` : ""}</td>
              <td className="px-2 text-xs">{t.reason}</td>
              <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{t.towed_from || "—"} → {t.towed_to || "—"}</td>
              <td className="px-2 text-xs">{t.tow_company || "—"}</td>
              <td className="px-2 font-mono text-[10px]">{t.incident_cc || "—"}</td>
              <td className="px-2">
                {t.status === "released"
                  ? <span className="font-mono text-[10px] font-bold text-[#10B981]">RELEASED{t.released_to ? ` → ${t.released_to}` : ""}</span>
                  : t.hold
                    ? <span className="font-mono text-[10px] font-bold text-[#F59E0B] flex items-center gap-1"><Lock size={10} /> HOLD</span>
                    : <span className="font-mono text-[10px] font-bold text-[#F97316]">IN TOW</span>}
              </td>
              <td className="px-2">
                <span className="flex items-center gap-1.5">
                <button data-testid={`tow-notice-${t.tow_number}`} title="Print owner-notification letter" onClick={async () => {
                  try {
                    const r = await client.get(`/tows/${t.id}/notice.pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url; a.download = `${t.tow_number}_owner_notice.pdf`;
                    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                  } catch { toast.error("Notice export failed"); }
                }} className="flex items-center gap-1 px-2 py-1 text-[11px] font-head border border-[#2C2D35] text-[#F97316] hover:border-[#F97316]">
                  <Printer size={12} /> Notice
                </button>
                {t.status !== "released" && (releasing === t.id ? (
                  <span className="flex items-center gap-1">
                    <input data-testid={`tow-release-to-${t.tow_number}`} value={releaseTo} onChange={(e) => setReleaseTo(e.target.value)}
                      placeholder="Released to…" className="bg-[#0d0e11] border border-[#2C2D35] px-2 py-1 text-[11px] font-mono w-32 outline-none" />
                    <button data-testid={`tow-release-confirm-${t.tow_number}`} onClick={() => release(t)} className="text-[#10B981]"><CheckCircle2 size={15} /></button>
                    <button onClick={() => setReleasing(null)} className="text-[#9CA3AF]"><X size={14} /></button>
                  </span>
                ) : (
                  <button data-testid={`tow-release-${t.tow_number}`} onClick={() => { setReleasing(t.id); setReleaseTo(""); }}
                    className="px-2 py-1 text-[11px] font-head border border-[#2C2D35] hover:border-[#10B981] hover:text-[#10B981]">Release</button>
                ))}
                </span>
              </td>
            </tr>
          ))}
          {data && !data.tows.length && <tr><td colSpan={9} className="p-4 text-xs text-[#9CA3AF] font-mono">No tow records. Use “Log Tow” to record a towed vehicle.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
